import React, { useEffect, useState } from 'react';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import './styles.scss';
import YesColumn from './YesColumn';
import NoColumn from './NoColumn';
import BestInterestColumn from './BestInterestColumn';
import { ACPConsentData, BestInterest } from './types';

interface ACPConsentProps {
  acpConsentDetails: ACPConsentData;
  setAcpConsentValues: (data: ACPConsentData) => void;
}

const ACPConsent: React.FC<ACPConsentProps> = ({
  acpConsentDetails,
  setAcpConsentValues,
}) => {
  const [showYesColumn, setShowYesColumn] = useState(false);
  const [showNoColumn, setShowNoColumn] = useState(false);
  const [noValue, setNoValue] = useState<string | null>(null);
  const [yesValue, setYesValue] = useState<string | null>(null);
  const [bestInterestOption, setBestInterestOption] = useState<string | null>(
    null
  );
  const [bestInterest, setBestInterest] = useState<BestInterest>({
    status: '',
    data: {},
  });
  const [bestInterestFromDetails, setBestInterestFromDetails] =
    useState<BestInterest>({
      status: '',
      data: {},
    });

  const toggleColumn = (column: 'yes' | 'no') => {
    if (column === 'yes') {
      setShowYesColumn(prev => !prev);
      if (showNoColumn) setShowNoColumn(false);
    } else if (column === 'no') {
      setShowNoColumn(prev => !prev);
      if (showYesColumn) setShowYesColumn(false);
    }
  };

  useEffect(() => {
    const noValueFromAcpConsentDetails = acpConsentDetails?.noValue;
    if (noValueFromAcpConsentDetails) {
      setShowNoColumn(true);
      setNoValue(noValueFromAcpConsentDetails);
    }
    const yesValueFromAcpConsentDetails = acpConsentDetails?.yesValue;
    if (yesValueFromAcpConsentDetails) {
      setShowYesColumn(true);
      setYesValue(yesValueFromAcpConsentDetails);
    }
  }, [acpConsentDetails?.noValue, acpConsentDetails?.yesValue]);

  useEffect(() => {
    const noValueFromDetails = acpConsentDetails?.noValue;
    if (noValueFromDetails) {
      setBestInterestOption(acpConsentDetails.bestInterest?.status || '');
      setBestInterestFromDetails(
        acpConsentDetails.bestInterest || { status: '', data: {} }
      );
    }
  }, [acpConsentDetails, acpConsentDetails?.noValue]);

  useEffect(() => {
    if (showYesColumn && yesValue) {
      setNoValue(null);
      setBestInterestOption(null);
      setBestInterest({
        status: '',
        data: {},
      });
    }
  }, [yesValue, showYesColumn]);

  useEffect(() => {
    if (showNoColumn && noValue) {
      setYesValue(null);
    }
  }, [noValue, showNoColumn]);

  useEffect(() => {
    const updatedAcpConsentDetails = () => {
      if (yesValue) {
        return {
          yesValue,
        };
      }
      if (noValue) {
        return {
          noValue,
          bestInterest,
        };
      }
      return null;
    };
    const updatedDetails = updatedAcpConsentDetails();
    if (updatedDetails) {
      setAcpConsentValues(updatedDetails);
    }
  }, [yesValue, noValue, bestInterest, setAcpConsentValues]);

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-l">Advance Care Planning Consent</h1>
      <p className="govuk-body">
        Do you consent to having an Advance Care Plan created for you and held
        by us?
      </p>
      <div className="flex flex-col">
        <div
          onClick={() => toggleColumn('yes')}
          className="govuk-body govuk-!-font-weight-bold w-fit cursor-pointer mb-3"
        >
          <span className="mr-2">
            {showYesColumn ? <MinusOutlined /> : <PlusOutlined />}
          </span>
          Yes
        </div>
        {showYesColumn && (
          <YesColumn
            yesValue={yesValue}
            setYesValue={setYesValue}
            setNoValue={setNoValue}
          />
        )}
        <div
          onClick={() => toggleColumn('no')}
          className="govuk-body govuk-!-font-weight-bold w-fit cursor-pointer mb-3"
        >
          <span className="mr-2">
            {showNoColumn ? <MinusOutlined /> : <PlusOutlined />}
          </span>
          No
        </div>
        {showNoColumn && <NoColumn noValue={noValue} setNoValue={setNoValue} />}
        <div
          onClick={() => toggleColumn('no')}
          className="govuk-body govuk-!-font-weight-bold w-fit cursor-pointer mb-3"
        >
          <span className="mr-2">
            {showNoColumn ? <MinusOutlined /> : <PlusOutlined />}
          </span>
          Best Interests
        </div>
        {showNoColumn && (
          <BestInterestColumn
            noValues={noValue}
            bestInterestOption={bestInterestOption}
            setBestInterestOption={setBestInterestOption}
            setBestInterest={setBestInterest}
            bestInterestFromDetails={bestInterestFromDetails}
          />
        )}
      </div>
    </div>
  );
};

export default ACPConsent;
